import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  House, CreditCard, Users, Receipt, Key, ShieldCheck, Broadcast, Wallet,
  ListChecks, SignOut, FileArrowUp, ChartBar, List,
} from "@phosphor-icons/react";
import { mAuth } from "@/lib/api";
import { MERCHANT } from "@/lib/routes";
import { StatusPill } from "@/components/Layout";
import { BlonixWordmark } from "@/components/BlonixLogo";
import { Sheet, SheetContent } from "@/components/ui/sheet";

const NAV = [
  { to: MERCHANT.home, label: "Dashboard", icon: House, end: true },
  { to: MERCHANT.wallet, label: "Wallet", icon: Wallet },
  { to: MERCHANT.cards, label: "Cards", icon: CreditCard },
  { to: MERCHANT.users, label: "Cardholders", icon: Users },
  { to: MERCHANT.transactions, label: "Transactions", icon: Receipt },
];
const NAV2 = [
  { to: MERCHANT.apiKeys, label: "API Keys", icon: Key },
  { to: MERCHANT.security, label: "Security", icon: ShieldCheck },
  { to: MERCHANT.webhooks, label: "Webhooks", icon: Broadcast },
  { to: MERCHANT.webhookEvents, label: "Webhook Log", icon: ListChecks },
  { to: MERCHANT.usage, label: "API Usage", icon: ChartBar },
  { to: MERCHANT.audit, label: "Audit Logs", icon: ListChecks },
  { to: MERCHANT.kyb, label: "KYB Documents", icon: FileArrowUp },
];

export default function MerchantLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = mAuth.user();
  const merchant = user?.merchant;
  const [navOpen, setNavOpen] = useState(false);

  const onLogout = async () => { await mAuth.logout(); navigate("/"); };

  return (
    <div className="min-h-screen flex bg-zinc-100" data-testid="merchant-shell">
      <aside className="hidden lg:flex w-60 shrink-0 bg-zinc-950 text-zinc-300 flex-col fixed inset-y-0 left-0 z-30">
        <MerchantSidebar user={user} merchant={merchant} onLogout={onLogout} />
      </aside>

      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent side="left" className="w-72 max-w-[85vw] p-0 bg-zinc-950 border-zinc-900 text-zinc-300 [&>button]:text-zinc-400 [&>button]:hover:text-white">
          <MerchantSidebar
            user={user}
            merchant={merchant}
            onLogout={onLogout}
            onNavigate={() => setNavOpen(false)}
          />
        </SheetContent>
      </Sheet>

      <div className="flex-1 min-w-0 flex flex-col lg:ml-60">
        <header className="lg:hidden sticky top-0 z-20 flex items-center gap-3 px-4 py-3 bg-white border-b border-zinc-200">
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            className="h-9 w-9 inline-flex items-center justify-center rounded-sm border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
            aria-label="Open navigation"
          >
            <List size={20} />
          </button>
          <BlonixWordmark className="h-4 w-auto" />
        </header>

        <main className="flex-1 min-w-0">
          <div key={location.pathname} className="page-enter">{children}</div>
        </main>
      </div>
    </div>
  );
}

function MerchantSidebar({ user, merchant, onLogout, onNavigate }) {
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-5 py-5 border-b border-zinc-900 shrink-0">
        <BlonixWordmark className="h-5 w-auto" />
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2">Merchant Portal</div>
      </div>
      <nav className="flex-1 overflow-y-auto sb-scroll px-2 py-4 space-y-6 min-h-0">
        <NavGroup items={NAV} onNavigate={onNavigate} />
        <NavGroup items={NAV2} onNavigate={onNavigate} />
      </nav>
      <div className="p-4 border-t border-zinc-900 shrink-0">
        <div className="text-xs text-zinc-400 truncate">{user?.email}</div>
        {merchant && (
          <div className="mt-2 flex flex-wrap gap-1">
            <StatusPill value={merchant.status} />
            <StatusPill value={merchant.kyb_status} />
          </div>
        )}
        <button type="button" onClick={onLogout} className="mt-3 w-full flex items-center gap-2 text-xs text-zinc-400 hover:text-white">
          <SignOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );
}

function NavGroup({ items, onNavigate }) {
  return (
    <ul className="space-y-0.5">
      {items.map(({ to, label, icon: Icon, end }) => (
        <li key={to}>
          <NavLink
            to={to}
            end={end}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm transition-colors ${
                isActive ? "bg-zinc-800 text-white" : "text-zinc-400 hover:text-white hover:bg-zinc-900"
              }`
            }
          >
            <Icon size={16} weight="duotone" />
            {label}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}
